import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface WoAction {
  id: string;
  name: string;
  note: string | null;
  is_active: boolean;
  org_id: string;
  action_type: string | null;
  action_subtype: string | null;
  tank_emptying: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateWoActionInput {
  name: string;
  note?: string;
  is_active?: boolean;
}

export interface UpdateWoActionInput {
  name?: string;
  note?: string | null;
  is_active?: boolean;
}

/**
 * Hook to fetch all WO actions for the current organization
 * @param activeOnly - If true, only returns active actions (default: true)
 */
export function useWoActions(activeOnly: boolean = true) {
  return useQuery({
    queryKey: ["wo_actions", activeOnly],
    queryFn: async () => {
      let query = supabase
        .from("wo_action")
        .select("*")
        .order("name", { ascending: true });

      if (activeOnly) {
        query = query.eq("is_active", true);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as WoAction[];
    },
  });
}

/**
 * Hook to create a new WO action
 */
export function useCreateWoAction() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateWoActionInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("wo_action")
        .insert([{
          name: input.name,
          note: input.note || null,
          is_active: input.is_active ?? true,
          org_id: userData.org_id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as WoAction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wo_actions"] });
      toast({
        title: "WO action created",
        description: "The work order action has been successfully created.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating WO action",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to update an existing WO action
 */
export function useUpdateWoAction() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateWoActionInput }) => {
      const { data, error } = await supabase
        .from("wo_action")
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as WoAction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wo_actions"] });
      toast({
        title: "WO action updated",
        description: "The work order action has been successfully updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating WO action",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * Hook to deactivate a WO action (soft delete)
 */
export function useDeleteWoAction() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("wo_action")
        .update({ is_active: false })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wo_actions"] });
      toast({
        title: "WO action deactivated",
        description: "The action has been deactivated and will no longer appear in dropdowns.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deactivating WO action",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
